import type {AriaLabelingProps} from '@react-types/shared';
import {useMemo} from 'react';
import {useInteractionModality} from '../interactions';
import {useDescription} from '../utils';
import {useLocalizedStringFormatter} from '../i18n';
import type {MultipleSelectionManager} from '../../@react-stately/selection';
import intlMessages from './intl';

export interface HighlightSelectionDescriptionProps {
  selectionManager: MultipleSelectionManager;
  hasItemActions?: boolean;
}

/**
 * Computes the description for a grid selectable collection.
 * @param props
 */
export function useHighlightSelectionDescription(
  props: HighlightSelectionDescriptionProps,
): AriaLabelingProps {
  const stringFormatter = useLocalizedStringFormatter(intlMessages);
  const modality = useInteractionModality();
  // null is the default if the user hasn't interacted with the table at all yet or the rest of the page
  const shouldLongPress =
    (modality === 'pointer' || modality === 'virtual' || modality == null) &&
    typeof window !== 'undefined' &&
    'ontouchstart' in window;

  const interactionDescription = useMemo(() => {
    const selectionMode = props.selectionManager.selectionMode;
    const selectionBehavior = props.selectionManager.selectionBehavior;

    let message = undefined;
    if (shouldLongPress) {
      message = stringFormatter.format('longPressToSelect');
    }

    return selectionBehavior === 'replace' &&
      selectionMode !== 'none' &&
      props.hasItemActions
      ? message
      : undefined;
  }, [
    props.selectionManager.selectionMode,
    props.selectionManager.selectionBehavior,
    props.hasItemActions,
    stringFormatter,
    shouldLongPress,
  ]);

  const descriptionProps = useDescription(interactionDescription ?? '');
  return descriptionProps;
}
